import {Helmet} from '@dr.pogodin/react-helmet';
import {BreadcrumbType, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBillingInformationListQuery, useGetBranchListQuery} from '@dms/api/core';
import {
  useGetEmployeeCostCenterQuery,
  usePatchEmployeeCostCenterMutation,
  usePostEmployeeCostCenterMutation,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {getBillingInformationOptions, handleApiError} from '@dms/shared';

import {ApiError, Nullish, useNavigate} from 'shared/web';

import {
  EmployeeCostCentersForm,
  EmployeeCostCentersFormSchema,
} from './types/EmployeeCostCentersFormSchema';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.employeeCostCenters.labels.title'),
    href: settingsRoutes.employeeCostCenters,
  },
];

export function EmployeeCostCentersDetail() {
  const navigate = useNavigate();
  const {id} = useParams();

  const [postEmployeeCostCenter] = usePostEmployeeCostCenterMutation();
  const [patchEmployeeCostCenter] = usePatchEmployeeCostCenterMutation();
  const {data, isError, isLoading} = useGetEmployeeCostCenterQuery(
    {id: id ?? ''},
    {skip: isNil(id)}
  );
  const {data: branches} = useGetBranchListQuery();
  const {data: billingInformations} = useGetBillingInformationListQuery({
    includeDeactivated: true,
  });

  const getBranchOptions = (selectedCompany: string | Nullish) => {
    if (isNilOrEmpty(selectedCompany)) {
      return undefined;
    }

    const companyName = billingInformations?.billingInformationListItems.find(
      (billingInformation) => billingInformation.id === selectedCompany
    )?.companyName;

    return branches?.branchListItems
      ?.filter((branch) => branch.companyName === companyName)
      .map((branch) => ({label: branch.marketingName, value: branch.id}));
  };

  const handleSubmit: FormSubmitHandler<EmployeeCostCentersForm> = async (
    body,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const request = isNil(id)
      ? postEmployeeCostCenter({body})
      : patchEmployeeCostCenter({body, id});
    await request
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.employeeCostCenters);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <>
      <Helmet
        title={i18n.t(
          isNil(id)
            ? 'entity.employeeCostCenters.labels.newCostCenter'
            : 'entity.employeeCostCenters.labels.title'
        )}
      />
      <SettingsTemplate
        header={{
          breadcrumbs,
          title: data?.description ?? i18n.t('entity.employeeCostCenters.labels.newCostCenter'),
        }}
        isError={isError}
        isLoading={isLoading}
        isCreating={isNil(id)}
        data-testid={testIds.settings.employeeCostCentersDetail('settingsTemplate')}
      >
        <Form<EmployeeCostCentersForm>
          defaultValues={{
            branch: data?.branch ?? undefined,
            company: data?.company ?? undefined,
            description: data?.description ?? undefined,
          }}
          experimentalZodSchema={EmployeeCostCentersFormSchema}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, formApi, navigateWithoutBlocker) => (
            <>
              <SettingsSection>
                <VStack spacing={4}>
                  <FormField
                    control={control}
                    type="text"
                    name="description"
                    isRequired
                    label={i18n.t('general.labels.description')}
                    data-testid={testIds.settings.employeeCostCentersDetail('description')}
                  />
                  <HStack spacing={4}>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        type="choice"
                        name="company"
                        isRequired
                        label={i18n.t('general.labels.company')}
                        options={getBillingInformationOptions({
                          billingInformations: billingInformations?.billingInformationListItems,
                          selectedId: formApi.watch('company'),
                          labelKey: 'companyName',
                        })}
                        onChange={() => formApi.setValue('branch', '')}
                        data-testid={testIds.settings.employeeCostCentersDetail('company')}
                      />
                    </Box>
                    <Box flex={1}>
                      <FormField
                        control={control}
                        type="choice"
                        name="branch"
                        isRequired
                        label={i18n.t('entity.branch.labels.branch')}
                        isDisabled={isNilOrEmpty(formApi.watch('company'))}
                        options={getBranchOptions(formApi.watch('company')) ?? []}
                        data-testid={testIds.settings.employeeCostCentersDetail('branch')}
                      />
                    </Box>
                  </HStack>
                </VStack>
              </SettingsSection>
              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    onClick: () => navigateWithoutBlocker(settingsRoutes.employeeCostCenters),
                    title: id
                      ? i18n.t('general.actions.discardChanges')
                      : i18n.t('general.actions.discard'),
                    variant: 'secondary',
                    'data-testid': testIds.settings.employeeCostCentersDetail('discard'),
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: id
                      ? i18n.t('general.actions.saveAndClose')
                      : i18n.t('general.actions.create'),
                    'data-testid': testIds.settings.employeeCostCentersDetail('save'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
